import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Buildings, Users, Cpu, ClockCounterClockwise, Plus,
  ShieldCheck, MagnifyingGlass, Storefront,
  TrashSimple, ArrowCounterClockwise, Archive, Package,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: "orgs", label: "Organizations", icon: Buildings },
  { key: "users", label: "Users & Roles", icon: Users },
  { key: "devices", label: "Devices", icon: Cpu },
  { key: "archived_shipments", label: "Archived Shipments", icon: Archive, superOnly: true },
  { key: "resellers", label: "Resellers", icon: Storefront },
  { key: "audit", label: "Audit Logs", icon: ClockCounterClockwise },
];

const ALL_ROLES = ["super_admin", "reseller_admin", "org_admin", "user", "viewer"];
const ORG_ADMIN_ROLES = ["org_admin", "user", "viewer"];   // non-super may assign these only

export default function AdminPanel() {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = ["super_admin", "admin"].includes(currentUser?.role);
  const allowedTabs = TABS.filter((t) => !t.superOnly || isSuperAdmin);
  const [tab, setTab] = useState("orgs");
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [resellers, setResellers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [search, setSearch] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const [orgRes, userRes, deviceRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/organizations`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/devices`),
        axios.get(`${API}/admin/stats`),
      ]);
      setOrgs(orgRes.data);
      setUsers(userRes.data);
      setDevices(deviceRes.data);
      setStats(statsRes.data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchResellers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/resellers/`);
      setResellers(res.data);
    } catch { /* may not have permission */ }
  }, []);

  const fetchAudit = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/audit-logs?limit=200`);
      setAuditLogs(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchData();
    fetchResellers();
    fetchAudit();
  }, [fetchData, fetchResellers, fetchAudit]);

  return (
    <div data-testid="admin-panel" className="space-y-4">
      {/* Stats Row */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Organizations", value: stats.organizations, icon: Buildings },
            { label: "Users", value: stats.users, icon: Users },
            { label: "Devices", value: stats.devices, icon: Cpu },
            { label: "Total Events", value: stats.total_events?.toLocaleString(), icon: ShieldCheck },
          ].map((s) => (
            <Card key={s.label} className="bg-zinc-900 border-zinc-800 p-3">
              <div className="flex items-center gap-2">
                <s.icon size={18} weight="duotone" className="text-teal-400" />
                <div>
                  <p className="text-lg font-mono font-semibold text-zinc-50">{s.value}</p>
                  <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.label}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-800 pb-1 overflow-x-auto">
        {allowedTabs.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-sm transition-colors whitespace-nowrap ${
              tab === key ? "bg-teal-500/10 text-teal-400" : "text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/50"
            }`}
            data-testid={`admin-tab-${key}`}
          >
            <Icon size={14} weight="duotone" />
            {label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === "orgs" && (
        <OrgsTab orgs={orgs} search={search} setSearch={setSearch}
          onRefresh={fetchData} showCreate={showCreateOrg} setShowCreate={setShowCreateOrg} />
      )}
      {tab === "users" && (
        <UsersTab users={users} orgs={orgs} search={search} setSearch={setSearch}
          onRefresh={fetchData} showCreate={showCreateUser} setShowCreate={setShowCreateUser}
          isSuperAdmin={isSuperAdmin} currentUser={currentUser} />
      )}
      {tab === "devices" && <DevicesTab devices={devices} search={search} setSearch={setSearch}
        onRefresh={fetchData} isSuperAdmin={isSuperAdmin} />}
      {tab === "archived_shipments" && isSuperAdmin && <ArchivedShipmentsTab />}
      {tab === "resellers" && <ResellersTab resellers={resellers} onRefresh={fetchResellers} />}
      {tab === "audit" && <AuditTab logs={auditLogs} />}
    </div>
  );
}

function OrgsTab({ orgs, search, setSearch, onRefresh, showCreate, setShowCreate }) {
  const [name, setName] = useState("");
  const [plan, setPlan] = useState("standard");

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/admin/organizations`, { name, plan });
      toast.success(`Organization "${name}" created`);
      setName(""); setShowCreate(false); onRefresh();
      // Tell the header OrgSwitcher to re-fetch so the new tenant shows up
      // immediately without requiring a page reload.
      window.dispatchEvent(new Event("holotrak:orgs-updated"));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const filtered = orgs.filter(o => !search || o.name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="relative w-64">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input placeholder="Search organizations..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-8 h-8 text-xs bg-zinc-950 border-zinc-800" />
        </div>
        <Button size="sm" onClick={() => setShowCreate(true)} className="bg-teal-500 hover:bg-teal-600 text-zinc-950">
          <Plus size={14} className="mr-1.5" />Create Org
        </Button>
      </div>
      <Card className="bg-zinc-900 border-zinc-800">
        <ScrollArea className="max-h-[500px]">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800">
                <TableHead className="text-xs text-zinc-500">Name</TableHead>
                <TableHead className="text-xs text-zinc-500">Plan</TableHead>
                <TableHead className="text-xs text-zinc-500">Status</TableHead>
                <TableHead className="text-xs text-zinc-500">Users</TableHead>
                <TableHead className="text-xs text-zinc-500">Devices</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(org => (
                <TableRow key={org.id} className="border-zinc-800 hover:bg-zinc-800">
                  <TableCell className="text-sm text-zinc-200 font-medium">{org.name}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px] border-zinc-700">{org.plan}</Badge></TableCell>
                  <TableCell>
                    <Badge className={`text-[10px] ${org.status === "active" ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"}`}>
                      {org.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs font-mono text-zinc-400">{org.user_count || 0}</TableCell>
                  <TableCell className="text-xs font-mono text-zinc-400">{org.device_count || 0}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollArea>
      </Card>
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-sm">
          <DialogHeader><DialogTitle className="text-zinc-50">Create Organization</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs text-zinc-400">Name</Label>
              <Input value={name} onChange={e => setName(e.target.value)} className="mt-1 bg-zinc-950 border-zinc-800" /></div>
            <div><Label className="text-xs text-zinc-400">Plan</Label>
              <Select value={plan} onValueChange={setPlan}>
                <SelectTrigger className="mt-1 bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                </SelectContent>
              </Select></div>
          </div>
          <Button onClick={handleCreate} className="w-full bg-teal-500 hover:bg-teal-600 text-zinc-950">Create</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UsersTab({ users, orgs, search, setSearch, onRefresh, showCreate, setShowCreate, isSuperAdmin, currentUser }) {
  const defaultOrgId = !isSuperAdmin ? (currentUser?.active_org_id || currentUser?.org_id || "") : "";
  const [form, setForm] = useState({ email: "", name: "", password: "", org_id: defaultOrgId, role: "user" });
  const availableRoles = isSuperAdmin ? ALL_ROLES : ORG_ADMIN_ROLES.concat(["reseller_admin"]).filter(
    (r) => r !== "super_admin",
  );
  // Non-super admins can only see/manage their own org
  const assignableOrgs = isSuperAdmin ? orgs : orgs.filter((o) => o.id === defaultOrgId);

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/admin/users`, { ...form, org_id: form.org_id || defaultOrgId });
      toast.success(`User "${form.email}" created`);
      setForm({ email: "", name: "", password: "", org_id: defaultOrgId, role: "user" });
      setShowCreate(false); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleStatus = async (u) => {
    const next = u.status === "active" ? "deactivate" : "activate";
    try {
      await axios.post(`${API}/admin/users/${u._id}/${next}`);
      toast.success(`User ${u.email} ${next}d`);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Could not ${next} user`);
    }
  };

  const changeRole = async (u, newRole) => {
    if (newRole === u.role) return;
    try {
      await axios.put(`${API}/admin/users/${u._id}`, { role: newRole });
      toast.success(`Role for ${u.email} changed to ${newRole}`);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update role");
    }
  };

  const filtered = users.filter(u => !search || u.email?.includes(search) || u.name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="relative w-64">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-8 h-8 text-xs bg-zinc-950 border-zinc-800"
            data-testid="admin-users-search" />
        </div>
        <Button size="sm" onClick={() => setShowCreate(true)} className="bg-teal-500 hover:bg-teal-600 text-zinc-950"
          data-testid="admin-create-user-btn">
          <Plus size={14} className="mr-1.5" />Create User
        </Button>
      </div>
      <Card className="bg-zinc-900 border-zinc-800">
        <ScrollArea className="max-h-[500px]">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800">
                <TableHead className="text-xs text-zinc-500">Name</TableHead>
                <TableHead className="text-xs text-zinc-500">Email</TableHead>
                <TableHead className="text-xs text-zinc-500">Role</TableHead>
                <TableHead className="text-xs text-zinc-500">Org</TableHead>
                <TableHead className="text-xs text-zinc-500">Status</TableHead>
                <TableHead className="text-xs text-zinc-500 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(u => {
                const isPlatform = ["super_admin", "admin"].includes(u.role);
                const canEdit = isSuperAdmin || !isPlatform;
                return (
                  <TableRow key={u._id} className="border-zinc-800 hover:bg-zinc-800" data-testid={`admin-user-row-${u.email}`}>
                    <TableCell className="text-sm text-zinc-200">{u.name}</TableCell>
                    <TableCell className="text-xs font-mono text-zinc-400">{u.email}</TableCell>
                    <TableCell>
                      {canEdit ? (
                        <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                          <SelectTrigger className="h-7 text-[11px] bg-zinc-950 border-zinc-800 w-32"
                            data-testid={`admin-user-role-${u.email}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {availableRoles.map(r => (
                              <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Badge className="text-[10px] bg-teal-500/20 text-teal-400">{u.role}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-zinc-500">{orgs.find(o => o.id === u.org_id)?.name || u.org_id?.substring(0, 8)}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1.5 text-[10px] font-mono ${u.status === "active" ? "text-emerald-400" : "text-zinc-500"}`}>
                        <span className={`w-2 h-2 rounded-full ${u.status === "active" ? "bg-emerald-400" : "bg-zinc-600"}`} />
                        {u.status || "active"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" disabled={!canEdit}
                        onClick={() => toggleStatus(u)}
                        className="h-7 text-[10px] border-zinc-700 hover:bg-zinc-800 disabled:opacity-30"
                        data-testid={`admin-user-toggle-${u.email}`}>
                        {u.status === "active" ? "Disable" : "Enable"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </ScrollArea>
      </Card>
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-sm">
          <DialogHeader><DialogTitle className="text-zinc-50">Create User</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs text-zinc-400">Email</Label>
              <Input value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} className="mt-1 bg-zinc-950 border-zinc-800"
                data-testid="admin-new-user-email" /></div>
            <div><Label className="text-xs text-zinc-400">Name</Label>
              <Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} className="mt-1 bg-zinc-950 border-zinc-800"
                data-testid="admin-new-user-name" /></div>
            <div><Label className="text-xs text-zinc-400">Password</Label>
              <Input type="password" value={form.password} onChange={e => setForm(p => ({...p, password: e.target.value}))} className="mt-1 bg-zinc-950 border-zinc-800"
                data-testid="admin-new-user-password" /></div>
            <div><Label className="text-xs text-zinc-400">Organization</Label>
              <Select value={form.org_id} onValueChange={v => setForm(p => ({...p, org_id: v}))}>
                <SelectTrigger className="mt-1 bg-zinc-950 border-zinc-800" data-testid="admin-new-user-org">
                  <SelectValue placeholder="Select org" />
                </SelectTrigger>
                <SelectContent>{assignableOrgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs text-zinc-400">Role</Label>
              <Select value={form.role} onValueChange={v => setForm(p => ({...p, role: v}))}>
                <SelectTrigger className="mt-1 bg-zinc-950 border-zinc-800" data-testid="admin-new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{availableRoles.map(r => <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <Button onClick={handleCreate} className="w-full bg-teal-500 hover:bg-teal-600 text-zinc-950"
            data-testid="admin-create-user-submit">Create User</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DevicesTab({ devices, search, setSearch, onRefresh, isSuperAdmin }) {
  const [confirmDelete, setConfirmDelete] = useState(null);   // device object
  const [deleting, setDeleting] = useState(false);

  const filtered = devices.filter(d => !search || d.device_name?.toLowerCase().includes(search.toLowerCase()) || d.name?.toLowerCase().includes(search.toLowerCase()));

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/devices/${confirmDelete.id}`);
      toast.success(`Device ${confirmDelete.device_name || confirmDelete.name} archived`);
      setConfirmDelete(null);
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <div className="relative w-64 mb-3">
        <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
        <Input placeholder="Search devices..." value={search} onChange={e => setSearch(e.target.value)}
          className="pl-8 h-8 text-xs bg-zinc-950 border-zinc-800"
          data-testid="admin-devices-search" />
      </div>
      <Card className="bg-zinc-900 border-zinc-800">
        <ScrollArea className="max-h-[500px]">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800">
                <TableHead className="text-xs text-zinc-500">Device</TableHead>
                <TableHead className="text-xs text-zinc-500">Type</TableHead>
                <TableHead className="text-xs text-zinc-500">Org</TableHead>
                <TableHead className="text-xs text-zinc-500">Status</TableHead>
                <TableHead className="text-xs text-zinc-500">Last Seen</TableHead>
                {isSuperAdmin && <TableHead className="text-xs text-zinc-500 text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={isSuperAdmin ? 6 : 5} className="text-xs text-zinc-600 text-center py-8">No devices registered yet. Devices are auto-discovered on data upload.</TableCell></TableRow>
              ) : filtered.map(d => (
                <TableRow key={d.id} className="border-zinc-800 hover:bg-zinc-800" data-testid={`admin-device-row-${d.device_name || d.name}`}>
                  <TableCell className="text-sm text-zinc-200 font-mono">{d.device_name || d.name}</TableCell>
                  <TableCell className="text-xs text-zinc-400">{d.device_type}</TableCell>
                  <TableCell className="text-xs text-zinc-500">{d.org_id?.substring(0, 8)}</TableCell>
                  <TableCell><Badge className={`text-[10px] ${d.status === "active" ? "bg-emerald-500/20 text-emerald-400" : "bg-zinc-700 text-zinc-400"}`}>{d.status}</Badge></TableCell>
                  <TableCell className="text-xs font-mono text-zinc-500">{d.last_seen || "—"}</TableCell>
                  {isSuperAdmin && (
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline"
                        onClick={() => setConfirmDelete(d)}
                        className="h-7 text-[10px] border-red-800/60 text-red-300 hover:bg-red-500/10"
                        data-testid={`admin-device-delete-${d.device_name || d.name}`}>
                        <TrashSimple size={11} className="mr-1" /> Delete
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollArea>
      </Card>

      {/* Delete confirmation */}
      <Dialog open={!!confirmDelete} onOpenChange={(open) => !open && !deleting && setConfirmDelete(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-md" data-testid="admin-device-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <TrashSimple size={16} className="text-red-400" /> Delete Device
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-zinc-400 py-2">
            Soft-delete device <span className="font-mono text-zinc-200">
              {confirmDelete?.device_name || confirmDelete?.name}
            </span>? Telemetry history, alerts, and shipment associations are <strong>retained</strong>.
            The device will be hidden from default listings.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" disabled={deleting} onClick={() => setConfirmDelete(null)}
              className="border-zinc-700 text-xs h-8">
              Cancel
            </Button>
            <Button disabled={deleting} onClick={doDelete}
              className="bg-red-500 hover:bg-red-600 text-zinc-950 text-xs h-8 font-semibold"
              data-testid="admin-device-delete-confirm">
              {deleting ? "Deleting…" : "Delete Device"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ArchivedShipmentsTab() {
  const [archived, setArchived] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [purgeConfirm, setPurgeConfirm] = useState("");
  const [working, setWorking] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/shipments/archived/list`);
      setArchived(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load archived shipments");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const restore = async (s) => {
    setWorking(true);
    try {
      await axios.post(`${API}/shipments/${s.id}/restore`);
      toast.success(`Restored "${s.name}"`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
    finally { setWorking(false); }
  };

  const purge = async () => {
    if (!purgeTarget || purgeConfirm !== purgeTarget.id) return;
    setWorking(true);
    try {
      await axios.delete(`${API}/shipments/${purgeTarget.id}/purge?confirm=${purgeTarget.id}`);
      toast.success(`Permanently deleted "${purgeTarget.name}"`);
      setPurgeTarget(null); setPurgeConfirm(""); refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purge failed");
    } finally { setWorking(false); }
  };

  return (
    <div data-testid="archived-shipments-tab">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs text-zinc-400 uppercase tracking-wider">Archived shipments — super admin only</h3>
        <Button size="sm" variant="outline" onClick={refresh}
          className="h-7 text-[11px] border-zinc-700">Refresh</Button>
      </div>
      <Card className="bg-zinc-900 border-zinc-800">
        <ScrollArea className="max-h-[500px]">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800">
                <TableHead className="text-xs text-zinc-500">Name</TableHead>
                <TableHead className="text-xs text-zinc-500">Archived</TableHead>
                <TableHead className="text-xs text-zinc-500">By</TableHead>
                <TableHead className="text-xs text-zinc-500">Status (at archive)</TableHead>
                <TableHead className="text-xs text-zinc-500 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-xs text-zinc-600 text-center py-8">Loading…</TableCell></TableRow>
              ) : archived.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-xs text-zinc-600 text-center py-8">No archived shipments.</TableCell></TableRow>
              ) : archived.map(s => (
                <TableRow key={s.id} className="border-zinc-800 hover:bg-zinc-800" data-testid={`archived-row-${s.id}`}>
                  <TableCell className="text-sm text-zinc-200 flex items-center gap-2">
                    <Package size={12} className="text-zinc-500" />{s.name}
                  </TableCell>
                  <TableCell className="text-[10px] font-mono text-zinc-500">{s.archived_at?.substring(0, 19)}</TableCell>
                  <TableCell className="text-xs font-mono text-zinc-400">{s.archived_by || "—"}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px] border-zinc-700">{s.status || "—"}</Badge></TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="outline" disabled={working}
                      onClick={() => restore(s)}
                      className="h-7 text-[10px] border-emerald-800/60 text-emerald-300 hover:bg-emerald-500/10"
                      data-testid={`archived-restore-${s.id}`}>
                      <ArrowCounterClockwise size={11} className="mr-1" /> Restore
                    </Button>
                    <Button size="sm" variant="outline" disabled={working}
                      onClick={() => { setPurgeTarget(s); setPurgeConfirm(""); }}
                      className="h-7 text-[10px] border-red-800/60 text-red-300 hover:bg-red-500/10"
                      data-testid={`archived-purge-${s.id}`}>
                      <TrashSimple size={11} className="mr-1" /> Purge
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollArea>
      </Card>

      <Dialog open={!!purgeTarget} onOpenChange={(open) => !open && !working && setPurgeTarget(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-md" data-testid="archived-purge-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <TrashSimple size={16} className="text-red-400" /> Permanently delete shipment
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-zinc-400 py-2">
            This will <strong>permanently delete</strong> shipment <span className="font-mono text-zinc-200">{purgeTarget?.name}</span>{" "}
            and its evidence packages and handoffs. Audit history is retained in the global audit
            log. This action <strong>cannot be undone</strong>.
          </p>
          <Label className="text-[10px] uppercase tracking-wider text-zinc-400">
            Type the shipment id to confirm:
          </Label>
          <Input value={purgeConfirm} onChange={(e) => setPurgeConfirm(e.target.value)}
            placeholder={purgeTarget?.id}
            data-testid="archived-purge-confirm-input"
            className="bg-zinc-950 border-zinc-800 mt-1 font-mono text-xs" />
          <p className="text-[10px] font-mono text-zinc-600 mt-1">{purgeTarget?.id}</p>
          <div className="flex justify-end gap-2 pt-3">
            <Button variant="outline" disabled={working} onClick={() => setPurgeTarget(null)}
              className="border-zinc-700 text-xs h-8">Cancel</Button>
            <Button disabled={working || purgeConfirm !== purgeTarget?.id}
              onClick={purge}
              className="bg-red-500 hover:bg-red-600 text-zinc-950 text-xs h-8 font-semibold"
              data-testid="archived-purge-confirm">
              {working ? "Purging…" : "Permanently delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ResellersTab({ resellers, onRefresh }) {
  return (
    <Card className="bg-zinc-900 border-zinc-800 p-4">
      <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-3">Reseller Partners</h3>
      {resellers.length === 0 ? (
        <p className="text-xs text-zinc-600 text-center py-8">No resellers configured yet.</p>
      ) : (
        <div className="space-y-2">
          {resellers.map(r => (
            <div key={r.id} className="flex items-center justify-between p-3 bg-zinc-950/50 rounded-sm">
              <div>
                <p className="text-sm text-zinc-200 font-medium">{r.name}</p>
                <p className="text-[10px] font-mono text-zinc-500">{r.email} | {r.org_ids?.length || 0} orgs</p>
              </div>
              <Badge className={`text-[10px] ${r.status === "active" ? "bg-emerald-500/20 text-emerald-400" : "bg-zinc-700"}`}>{r.status}</Badge>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function AuditTab({ logs }) {
  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <ScrollArea className="max-h-[500px]">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800">
              <TableHead className="text-xs text-zinc-500">Time</TableHead>
              <TableHead className="text-xs text-zinc-500">User</TableHead>
              <TableHead className="text-xs text-zinc-500">Action</TableHead>
              <TableHead className="text-xs text-zinc-500">Resource</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-xs text-zinc-600 text-center py-8">No audit logs yet.</TableCell></TableRow>
            ) : logs.map((log, i) => (
              <TableRow key={i} className="border-zinc-800 hover:bg-zinc-800">
                <TableCell className="text-[10px] font-mono text-zinc-500">{log.timestamp?.substring(0, 19)}</TableCell>
                <TableCell className="text-xs text-zinc-400">{log.details?.user_email || log.user_id?.substring(0, 8)}</TableCell>
                <TableCell><Badge variant="outline" className="text-[10px] border-zinc-700">{log.action}</Badge></TableCell>
                <TableCell className="text-xs text-zinc-500">{log.resource_type} {log.resource_id?.substring(0, 8)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ScrollArea>
    </Card>
  );
}
